import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { UPDATE_PLAYBACK_OVERRIDE } from '@optools/services/operator-gateway/overrides/playback/mutations';
import { PlaybackOverride } from '@optools/services/operator-gateway/overrides/playback/types';
import { ClientType, MatchParams } from '@optools/services/operator-gateway/overrides/types';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { PlaybackOverrideForm, PlaybackOverrideFormValues } from './playback-override-form';

export interface UpdatePlaybackOverrideProps {
  override: PlaybackOverride | null;
}

export function UpdatePlaybackOverride({ override }: UpdatePlaybackOverrideProps) {
  const [open, setOpen] = useState(false);

  const [updatePlaybackOverride, { loading }] = useMutation(UPDATE_PLAYBACK_OVERRIDE, {
    onCompleted: ({ updatePlaybackOverride }) => {
      if (!updatePlaybackOverride) {
        toast.error('Update Override Error', {
          description: 'Failed to update override, please try again',
          duration: Infinity,
        });

        return;
      } else {
        toast.success('Playback Override successfully updated');
      }

      setOpen(false);
    },
  });

  const handleClose = (nextOpen: boolean) => {
    if (loading) return;
    setOpen(nextOpen);
  };

  const handleSubmit = (values: PlaybackOverrideFormValues) => {
    if (!override) return;
    const matchParams: MatchParams = {};
    const { clientType, experience, features, userId } = values;
    const id = +override.id;

    if (clientType !== ClientType.ALL_CLIENTS) matchParams.clientType = clientType;
    if (userId) matchParams.userId = userId;

    updatePlaybackOverride({
      update: (cache, { data }) => {
        if (!data?.updatePlaybackOverride) return;

        cache.writeFragment({
          data: { experience, features, id, matchParams },
          fragment: gql`
            fragment PlaybackOverride on ExperienceOverrideSearchView {
              id
              features
              matchParams
              experience
            }
          `,
          id: cache.identify({ __typename: 'ExperienceOverrideSearchView', id: override.id }),
        });
      },
      variables: { experience, features, id, matchParams },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button size="icon" title="Update Override" variant="outline">
          <Pencil />
          <span className="sr-only">Update Override</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Override</DialogTitle>
          <DialogDescription className="sr-only">Update an existing override</DialogDescription>
        </DialogHeader>
        <PlaybackOverrideForm loading={loading} override={override} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
}
